import { useState, useEffect } from "react";
import { getThemeParams } from "./themeStorage";

/**
 * Interface for widget theme URL parameters
 *
 * Two shapes are supported, mutually exclusive:
 * - V1: `themeId` (legacy widget theme row PK on `bc-brand-app-theme`).
 * - V2: `appId` + `scope` + `version` (opaque V2 app id like
 *   `app_petarmor_dtc`, plus `dtc`/`widget` scope and a `vN` version).
 *   Portal opens V2-shape URLs going forward.
 *
 * The editor treats either shape as "a theme target exists"; downstream
 * write calls branch on which shape the URL carries (see
 * `widgetThemeApiV2.getV2EditTarget`).
 */
export interface WidgetThemeParams {
  themeId: string | null;
  editMode: boolean;
  editThemeToken: string | null;
  origin: string | null;
  previewMode: boolean;
  appId: string | null;
  scope: string | null;
  version: string | null;
}

/** V1 `themeId` or V2 `appId`+`scope`+`version`, plus an edit token. */
export const hasEditableThemeTarget = (params: WidgetThemeParams): boolean =>
  Boolean(
    params.editThemeToken &&
      (params.themeId || (params.appId && params.scope && params.version))
  );

/**
 * Stable React Query cache key for the active theme target.
 * V1 → themeId; V2 → `appId:scope:version`; none → null.
 */
export const getThemeCacheKey = (params: WidgetThemeParams): string | null => {
  if (params.themeId) return params.themeId;
  if (params.appId && params.scope && params.version) {
    return `${params.appId}:${params.scope}:${params.version}`;
  }
  return null;
};

/**
 * Helper to calculate theme params from URL and localStorage
 */
const calculateThemeParams = (): WidgetThemeParams => {
  // Check URL params first (priority)
  const urlParams = new URLSearchParams(window.location.search);
  const urlThemeId = urlParams.get("themeId");
  const urlEditMode = urlParams.get("editMode");
  const urlEditThemeToken = urlParams.get("editThemeToken");
  const urlOrigin = urlParams.get("origin");
  const urlPreviewMode = urlParams.get("previewMode");
  const urlAppId = urlParams.get("appId");
  const urlScope = urlParams.get("scope");
  const urlVersion = urlParams.get("version");

  // Get localStorage params as fallback
  const storageParams = getThemeParams();

  const previewMode =
    urlPreviewMode === "true" ||
    (urlPreviewMode === null && storageParams.previewMode);

  return {
    themeId: urlThemeId || storageParams.themeId,
    editMode:
      !previewMode &&
      (urlEditMode === "true" ||
        (urlEditMode === null && storageParams.editMode)),
    editThemeToken: urlEditThemeToken || storageParams.editThemeToken,
    origin: urlOrigin,
    previewMode,
    appId: urlAppId || storageParams.appId,
    scope: urlScope || storageParams.scope,
    version: urlVersion || storageParams.version,
  };
};

/**
 * Hook to extract widget theme parameters from URL or localStorage
 * Used by widgets to determine if they should load theme config and enable editing
 *
 * Priority: URL parameters > localStorage
 *
 * This hook is reactive - it will re-check localStorage after mount to handle
 * cases where localStorage is set after the initial render (e.g., by ThemeManager).
 *
 * @returns WidgetThemeParams object containing themeId, editMode, editThemeToken, and origin
 *
 * @example
 * const { themeId, editMode, editThemeToken, origin } = useWidgetThemeParams();
 * if (themeId) {
 *   // Fetch theme configuration
 * }
 */
export const useWidgetThemeParams = (): WidgetThemeParams => {
  const [params, setParams] = useState<WidgetThemeParams>(calculateThemeParams);

  // Re-check localStorage after mount to catch cases where localStorage
  // is set after initial render (e.g., by ThemeManager's useEffect)
  useEffect(() => {
    // Small delay to allow other useEffects (like ThemeManager's syncUrlToStorage) to run first
    const timeoutId = setTimeout(() => {
      const currentParams = calculateThemeParams();
      setParams((prev) => {
        // Only update if values actually changed
        if (
          prev.themeId !== currentParams.themeId ||
          prev.editMode !== currentParams.editMode ||
          prev.editThemeToken !== currentParams.editThemeToken ||
          prev.origin !== currentParams.origin ||
          prev.previewMode !== currentParams.previewMode ||
          prev.appId !== currentParams.appId ||
          prev.scope !== currentParams.scope ||
          prev.version !== currentParams.version
        ) {
          return currentParams;
        }
        return prev;
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, []);

  return params;
};
